type ScrollAndFlashOptions = {
  scrollBehavior?: ScrollBehavior;
  scrollBlock?: ScrollLogicalPosition;
  maxWaitMs?: number;
  stableFrameCount?: number;
  movementThresholdPx?: number;
  animationDurationMs?: number;
};

const DEFAULT_OPTIONS: Required<ScrollAndFlashOptions> = {
  scrollBehavior: "smooth",
  scrollBlock: "center",
  maxWaitMs: 3000,
  stableFrameCount: 6,
  movementThresholdPx: 0.5,
  animationDurationMs: 900,
};

const FLASH_KEYFRAMES: Keyframe[] = [
  {
    boxShadow: "0 0 0 0 rgba(66, 153, 225, 0)",
    backgroundColor: "transparent",
  },
  {
    boxShadow: "0 0 0 8px rgba(66, 153, 225, 0.35)",
    backgroundColor: "rgba(66, 153, 225, 0.12)",
  },
  {
    boxShadow: "0 0 0 0 rgba(66, 153, 225, 0)",
    backgroundColor: "transparent",
  },
];

export function scrollToElementAndFlash(element: HTMLElement, options: ScrollAndFlashOptions = {}) {
  const {
    scrollBehavior,
    scrollBlock,
    maxWaitMs,
    stableFrameCount,
    movementThresholdPx,
    animationDurationMs,
  } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  let hasFlashed = false;

  const flashOnce = () => {
    if (hasFlashed) return;
    hasFlashed = true;

    if (typeof element.animate === "function") {
      element.animate(FLASH_KEYFRAMES, {
        duration: animationDurationMs,
        easing: "ease-out",
      });
      return;
    }

    // Fallback for environments without Web Animations support.
    const previousTransition = element.style.transition;
    const previousBoxShadow = element.style.boxShadow;
    const previousBackgroundColor = element.style.backgroundColor;

    element.style.transition = "box-shadow 200ms ease-out, background-color 200ms ease-out";
    element.style.boxShadow = "0 0 0 8px rgba(66, 153, 225, 0.35)";
    element.style.backgroundColor = "rgba(66, 153, 225, 0.12)";

    globalThis.setTimeout(() => {
      element.style.boxShadow = previousBoxShadow;
      element.style.backgroundColor = previousBackgroundColor;
      element.style.transition = previousTransition;
    }, animationDurationMs);
  };

  element.scrollIntoView({ behavior: scrollBehavior, block: scrollBlock });

  let stableFrames = 0;
  let lastTop = element.getBoundingClientRect().top;
  const startedAt = Date.now();

  const animateWhenScrollSettles = () => {
    const currentTop = element.getBoundingClientRect().top;
    const isStillMoving = Math.abs(currentTop - lastTop) > movementThresholdPx;
    lastTop = currentTop;

    if (isStillMoving) {
      stableFrames = 0;
    } else {
      stableFrames += 1;
    }

    const timedOut = Date.now() - startedAt > maxWaitMs;
    if (stableFrames >= stableFrameCount || timedOut) {
      flashOnce();
      return;
    }

    globalThis.requestAnimationFrame(animateWhenScrollSettles);
  };

  globalThis.requestAnimationFrame(animateWhenScrollSettles);
}
